import { InfoOutlineIcon } from '@chakra-ui/icons';
import { Box, Flex, Divider, Heading, Text, Tooltip } from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';

import {
  utils as commonUtils,
  finleyAssertUnreachable,
  error as commonError,
} from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { ReportDataCellObjectConfigContainer } from './dataCellObjectConfig/ReportDataCellObjectConfigContainer';
import { EditSheetPropertyModal } from './EditSheetPropertyModal';
import { ReportFileInsertionConfigContainer } from './fileInsertionConfig/ReportFileInsertionConfigContainer';
import {
  AddIconButton,
  ResetIconButton,
  EditIconButton,
  DeleteIconButton,
} from 'packages/common-frontend/components/IconButton';
import {
  Select,
  formatSelectOptions,
} from 'packages/common-frontend/components/Select';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';

type DataSheetOutput = CreditAgreement.Config.Reporting.Outputs.Xlsx.DataSheetOutput;
type DataOutputType = CreditAgreement.Config.Reporting.Outputs.Xlsx.DataOutputType;

type ReportXlsxOutputSheetConfigWrapperProps = {
  organizationId: string;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  sheetIdxPropertyKey: string;
  targetSheet: DataSheetOutput;
  queryResults: CreditAgreement.Config.QueryResult[];
  bankAccounts: CreditAgreement.DomainEntity.Items.BankAccountEntity[];
};
const ReportXlsxOutputSheetConfigWrapper = ({
  organizationId,
  wipConfig,
  sheetIdxPropertyKey,
  targetSheet,
  queryResults,
  bankAccounts,
}: ReportXlsxOutputSheetConfigWrapperProps) => {
  const sheetIdxConfigPropertyKey = `${sheetIdxPropertyKey}.config`;

  const { type } = targetSheet;
  switch (type) {
    case 'dataCellObject':
      return (
        <ReportDataCellObjectConfigContainer
          wipConfig={wipConfig}
          sheetIdxConfigPropertyKey={sheetIdxConfigPropertyKey}
          targetSheetConfig={targetSheet.config}
          queryResults={queryResults}
        />
      );
    case 'imageFileInsertion':
    case 'fileInsertion':
      return (
        <ReportFileInsertionConfigContainer
          organizationId={organizationId}
          wipConfig={wipConfig}
          sheetIdxConfigPropertyKey={sheetIdxConfigPropertyKey}
          targetSheetConfig={targetSheet.config}
          bankAccounts={bankAccounts}
        />
      );
    default:
      return finleyAssertUnreachable(type);
  }
};

const defaultFileInsertionConfig = (): CreditAgreement.Config.Reporting.Outputs.Xlsx.FileInsertionConfig => ({
  fileType: 'rawMasterLoanTape',
  loanProductId: '',
  reportingDateFormula: 'reportingDate',
  startingExcelCoordinate: 'A1',
});

const defaultDataCellObjectConfig = (): CreditAgreement.Config.Reporting.Outputs.Xlsx.DataCellObjectConfig => ({
  data: [],
});

const newDataSheetOutput = (idx: number): DataSheetOutput => ({
  name: `sheet${idx}`,
  type: 'dataCellObject',
  config: defaultDataCellObjectConfig(),
});

type ReportXlsxOutputSheetsContainerProps = {
  organizationId: string;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  sheets: DataSheetOutput[];
  sheetsPropertyKey: string;
  targetSheetIdx: number;
  setTargetSheetIdx: React.Dispatch<React.SetStateAction<number>>;
  queryResults: CreditAgreement.Config.QueryResult[];
  bankAccounts: CreditAgreement.DomainEntity.Items.BankAccountEntity[];
};
export const ReportXlsxOutputSheetsContainer = ({
  organizationId,
  wipConfig,
  sheetsPropertyKey,
  sheets,
  targetSheetIdx,
  setTargetSheetIdx,
  queryResults,
  bankAccounts,
}: ReportXlsxOutputSheetsContainerProps) => {
  const targetSheet = sheets[targetSheetIdx];
  const sheetIdxPropertyKey = `${sheetsPropertyKey}[${targetSheetIdx}]`;

  const [isEditSheetModalOpen, setIsEditSheetModalOpen] = useState<boolean>(
    false,
  );
  const [sheetTypeState, setSheetTypeState] = useState<DataOutputType>(
    targetSheet.type,
  );

  const onUpdateSheetType = (): void => {
    if (!wipConfig?.isMutateAllowed(sheetIdxPropertyKey)) {
      return;
    }

    let updatedSheet: DataSheetOutput;
    switch (sheetTypeState) {
      case 'dataCellObject':
        updatedSheet = {
          name: targetSheet.name,
          type: sheetTypeState,
          config: defaultDataCellObjectConfig(),
        };
        break;
      case 'imageFileInsertion':
      case 'fileInsertion':
        updatedSheet = {
          name: targetSheet.name,
          type: sheetTypeState,
          config: defaultFileInsertionConfig(),
        };
        break;
      default:
        throw new commonError.FinleyError(
          `Reached never state when updating sheet type`,
        );
    }

    wipConfig.formProps.setFieldValue(sheetIdxPropertyKey, updatedSheet);
  };

  useEffect(() => {
    setSheetTypeState(targetSheet.type);
  }, [targetSheet.type]);

  return (
    <Box id="reporting-xlsx-sheets-container" borderWidth="1px" p="4">
      <Box mb="6">
        <Heading fontSize="large" as="h4" mb="2">
          Sheets Config
        </Heading>
        {wipConfig?.isMutateAllowed(sheetsPropertyKey) && (
          <Text fontStyle="italic" fontSize="sm">
            Below output configuration will be evaluated at generation and
            override existing template
          </Text>
        )}
      </Box>

      <Flex id="sheet-tabs-container">
        {sheets.map(({ name }, idx) => {
          const isTargetSheet = targetSheetIdx === idx;
          return (
            <Flex
              key={`sheet-tab-${name}`}
              alignItems="center"
              justifyContent="space-between"
              border="1px"
              borderColor={isTargetSheet ? 'blue.500' : 'gray.700'}
              borderBottom="none"
              roundedTop="lg"
              w="72"
              onClick={(_evt) => {
                if (targetSheetIdx !== idx) {
                  setTargetSheetIdx(idx);
                }
              }}
              _hover={{ cursor: isTargetSheet ? 'default' : 'grab' }}
            >
              <Text
                px="3"
                py="2"
                fontWeight={isTargetSheet ? 'bold' : undefined}
                border="none"
              >
                {name}
              </Text>
              {wipConfig?.isMutateAllowed(sheetsPropertyKey) && isTargetSheet && (
                <Flex alignItems="center">
                  <EditIconButton
                    ariaLabel="edit sheet"
                    label="Edit Sheet"
                    onClick={() => {
                      setIsEditSheetModalOpen(true);
                    }}
                  />
                  {sheets.length > 1 && (
                    <DeleteIconButton
                      ariaLabel="delete sheet"
                      label="Delete Sheet"
                      onClick={() => {
                        // Remove target sheet from the WIP config
                        const updatedSheets = sheets.filter(
                          (_, loopIdx) => loopIdx !== targetSheetIdx,
                        );
                        wipConfig.formProps.setFieldValue(
                          sheetsPropertyKey,
                          updatedSheets,
                        );

                        // Update targetIdx to new value since deleting current
                        const newTargetIdx = Math.max(targetSheetIdx - 1, 0);
                        setTargetSheetIdx(newTargetIdx);
                      }}
                    />
                  )}
                </Flex>
              )}
            </Flex>
          );
        })}

        {wipConfig?.isMutateAllowed(sheetsPropertyKey) && (
          <AddIconButton
            ariaLabel="add sheet"
            label="Add Sheet"
            onClick={() => {
              const updatedSheets: DataSheetOutput[] = [
                ...sheets,
                newDataSheetOutput(sheets.length),
              ];

              wipConfig.formProps.setFieldValue(
                sheetsPropertyKey,
                updatedSheets,
              );
            }}
          />
        )}
      </Flex>

      <Divider mb="4" />

      <Flex alignItems="center" mb="4">
        <Flex alignItems="center" w="36">
          <Text fontWeight="semibold" mr="2">
            Sheet Type
          </Text>
          {targetSheet.type !== sheetTypeState && (
            <Tooltip label="New sheet type unsaved">
              <InfoOutlineIcon />
            </Tooltip>
          )}
        </Flex>

        {wipConfig?.isMutateAllowed(sheetIdxPropertyKey) ? (
          <>
            <Box w="80" mr="3">
              <Select<DataOutputType>
                value={sheetTypeState}
                onChange={(val) => {
                  if (val) {
                    setSheetTypeState(val);
                  }
                }}
                options={formatSelectOptions(
                  CreditAgreement.Config.Reporting.Outputs.Xlsx
                    .DataOutputTypeIterable,
                  'title',
                )}
              />
            </Box>
            <ResetIconButton
              ariaLabel="reset sheet type"
              label="Reset Sheet Type"
              onClick={(_evt) => onUpdateSheetType()}
              isDisabled={targetSheet.type === sheetTypeState}
            />
          </>
        ) : (
          <Text>{commonUtils.toTitleCase(targetSheet.type)}</Text>
        )}
      </Flex>

      <ReportXlsxOutputSheetConfigWrapper
        organizationId={organizationId}
        wipConfig={wipConfig}
        targetSheet={targetSheet}
        sheetIdxPropertyKey={sheetIdxPropertyKey}
        queryResults={queryResults}
        bankAccounts={bankAccounts}
      />

      <EditSheetPropertyModal
        wipConfig={wipConfig}
        targetSheet={targetSheet}
        sheetIdxPropertyKey={sheetIdxPropertyKey}
        isOpen={isEditSheetModalOpen}
        onCancel={() => {
          setIsEditSheetModalOpen(false);
        }}
      />
    </Box>
  );
};
